import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  XMarkIcon, 
  CloudArrowUpIcon, 
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { importOmbudsmanDecisions } from '../../utils/api';
import { OmbudsmanImportResponse } from '../../types/ombudsman';
import { useWebSocket } from '../../contexts/WebSocketContext';

interface OmbudsmanImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportProgress {
  status: string;
  processed_count: number;
  total_count: number;
  failed_count?: number;
  progress_percentage: number;
  current_operation: string;
  error_message?: string;
}

const OmbudsmanImportModal: React.FC<OmbudsmanImportModalProps> = ({ onClose, onSuccess }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<OmbudsmanImportResponse | null>(null);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { isConnected, addMessageListener } = useWebSocket();

  useEffect(() => {
    const remove = addMessageListener((message: any) => {
      if (message.type === 'ombudsman.import.progress' && message.batch_id === currentBatchId && currentBatchId) {
        const progressData = message.data;
        setImportProgress(progressData);

        // Check if import is completed
        if (progressData.status === 'completed' || progressData.status === 'partial' || progressData.status === 'failed') {
          setImporting(false);
          setImportResult({
            batch_id: currentBatchId,
            status: progressData.status,
            filename: selectedFile?.name || '',
            total_decisions: progressData.total_count,
            imported_decisions: progressData.processed_count,
            failed_decisions: progressData.failed_count || 0,
            error_message: progressData.error_message
          });

          if (progressData.status === 'completed') {
            toast.success(`Successfully imported ${progressData.processed_count} ombudsman decisions`);
          } else if (progressData.status === 'partial') {
            toast.warning(`Import partially completed: ${progressData.processed_count} successful, ${progressData.failed_count} failed`);
          } else if (progressData.status === 'failed') {
            toast.error(`Import failed: ${progressData.error_message}`);
          }
        }
      }
    });
    return remove;
  }, [addMessageListener, currentBatchId, selectedFile]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setSelectedFile(file);
      } else {
        toast.error('Please select a JSON file');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setSelectedFile(file);
      } else {
        toast.error('Please select a JSON file');
      }
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImporting(true);
    setImportProgress({
      status: 'pending',
      processed_count: 0,
      total_count: 0,
      progress_percentage: 0,
      current_operation: 'Starting import...'
    });
    
    try {
      const response: OmbudsmanImportResponse = await importOmbudsmanDecisions(selectedFile);
      
      if (response.batch_id && response.status === 'pending') {
        // Background processing started
        setCurrentBatchId(response.batch_id);
        setImportProgress({
          status: 'processing',
          processed_count: 0,
          total_count: response.total_decisions || 0,
          progress_percentage: 0,
          current_operation: response.message || 'Processing...'
        });
        toast.info(`Import started. Processing ${response.total_decisions} decisions...`);
      } else {
        // Fallback for immediate responses (sync processing)
        setImportResult(response);
        setImporting(false);
        
        if (response.status === 'completed') {
          toast.success(`Successfully imported ${response.imported_decisions} ombudsman decisions`);
        } else if (response.status === 'partial') {
          toast.warning(`Import partially completed: ${response.imported_decisions} successful, ${response.failed_decisions} failed`);
        } else if (response.status === 'failed') {
          toast.error(`Import failed: ${response.error_message}`);
        }
      }
    } catch (error) {
      console.error('Error starting import:', error);
      toast.error('Failed to start import');
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleClose = () => {
    if (importResult && (importResult.status === 'completed' || importResult.status === 'partial')) {
      onSuccess();
    } else {
      onClose();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'partial':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-6 w-6 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-800 bg-green-100';
      case 'partial':
        return 'text-yellow-800 bg-yellow-100';
      case 'failed':
        return 'text-red-800 bg-red-100';
      default:
        return 'text-blue-800 bg-blue-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Import Ombudsman Decisions
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!importResult ? (
          <>
            {/* Progress Bar (shown during import) */}
            {importing && importProgress && (
              <div className="mb-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Import Progress
                  </span>
                  <span className="text-sm text-gray-500">
                    {importProgress.progress_percentage}%
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${importProgress.progress_percentage}%` }}
                  ></div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {importProgress.current_operation}
                  </span>
                  <span className="text-gray-500">
                    {importProgress.processed_count} / {importProgress.total_count}
                  </span>
                </div>
                
                {/* WebSocket Connection Status */}
                {isConnected ? (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    Connected - receiving live updates
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                    WebSocket disconnected - reconnecting...
                  </div>
                )}
              </div>
            )}

            {/* File Upload Area */}
            <div className="mb-6">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragActive 
                    ? 'border-blue-400 bg-blue-50' 
                    : selectedFile 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  className="hidden"
                />
                
                {selectedFile ? (
                  <div className="space-y-2">
                    <DocumentTextIcon className="mx-auto h-12 w-12 text-green-500" />
                    <div className="text-sm font-medium text-green-800">
                      {selectedFile.name}
                    </div>
                    <div className="text-xs text-green-600">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="text-gray-600">
                      <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                    </div>
                    <div className="text-xs text-gray-500">
                      JSON files only
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-medium text-blue-800 mb-2">
                Expected JSON Format
              </h3>
              <div className="text-xs text-blue-700 space-y-1">
                <p>• File should contain a "decisions" array with ombudsman decision objects</p>
                <p>• Each decision should have: reference, decision_date, decision_status, industry_sector, summary</p>
                <p>• Optional fields: business_name, full_text, pdf_url, page_count</p>
                <p>• The system will automatically extract complaint sections and generate embeddings</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={importing}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!selectedFile || importing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {importProgress?.current_operation || 'Processing...'}
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="h-4 w-4" />
                    Import Decisions
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          /* Import Results */
          <div className="space-y-6">
            <div className="text-center">
              {getStatusIcon(importResult.status)}
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                Import {importResult.status === 'completed' ? 'Completed' : importResult.status === 'partial' ? 'Partially Completed' : 'Failed'}
              </h3>
            </div>

            {/* Import Statistics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {importResult.total_decisions}
                </div>
                <div className="text-sm text-blue-800">Total</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {importResult.imported_decisions}
                </div>
                <div className="text-sm text-green-800">Imported</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {importResult.failed_decisions}
                </div>
                <div className="text-sm text-red-800">Failed</div>
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex justify-center">
              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(importResult.status)}`}>
                {importResult.status.charAt(0).toUpperCase() + importResult.status.slice(1)}
              </span>
            </div>

            {/* Error Message */}
            {importResult.error_message && (
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="text-sm text-red-800">
                  <strong>Error:</strong> {importResult.error_message}
                </div>
              </div>
            )}

            {/* Success Message */}
            {importResult.status === 'completed' && (
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-sm text-green-800">
                  All ombudsman decisions have been successfully imported and are ready for intelligent signal matching.
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OmbudsmanImportModal;
